import { Alert, CommonProp, Select } from '@acin/ui-core';
import { SelectOptionValue } from '@acin/ui-core/types/src/components/Select/components/Select';
import React, { useRef, useState } from 'react';
import { IInternalRegulationObligation, IInternalRegulationObligationReviewStatus } from '../../../graphql/generated';
import { REGULATION_TABS } from '../../../helpers';
import { useIntersectionObserver } from '../../../hooks';
import { CheckboxCard } from '../../CheckboxCard';
import { useRegulation } from '../../RegulationProvider';
import { StatusBadge } from '../../StatusBadge';
import { IInternalRegulationObligationsQuery } from '../graphql/queries/internalRegulationObligations.generated';
import { useObligations } from '../hooks/useObligations';
import * as SC from './ObligationsList.styled';

export interface ObligationsListProps extends CommonProp {
  className?: string;
  activeObligation?: IInternalRegulationObligation | undefined;
  onUpdateActiveObligation?: (obligation: IInternalRegulationObligation | undefined) => void;
  selectedObligations?: Array<IInternalRegulationObligation>;
  onToggleSelectedObligation?: (obligation: IInternalRegulationObligation) => void;
  useReviewStatus?: boolean;
  selectionMode?: 'checkbox' | 'radio';
  onCompleted?: (data: IInternalRegulationObligationsQuery) => void;
  // Obligations to force-display as Reviewed (e.g. all their rules have been confirmed).
  reviewedObligationIds?: Set<string>;
}

const statusLabelMap: Record<IInternalRegulationObligationReviewStatus, string> = {
  [IInternalRegulationObligationReviewStatus.ToReview]: 'To Review',
  [IInternalRegulationObligationReviewStatus.InProgress]: 'In Progress',
  [IInternalRegulationObligationReviewStatus.Reviewed]: 'Reviewed',
};

const statusFilterOptions = [
  { value: 'ALL', label: 'All' },
  ...Object.values(IInternalRegulationObligationReviewStatus).map((value) => ({
    value,
    label: statusLabelMap[value],
  })),
];

export const ObligationsList: React.FC<ObligationsListProps> = ({
  className,
  selectedObligations,
  onToggleSelectedObligation,
  activeObligation,
  onUpdateActiveObligation,
  selectionMode = 'checkbox',
  onCompleted,
  reviewedObligationIds,
}) => {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const { activeRegulationTabId } = useRegulation();
  const [statusFilter, setStatusFilter] = useState<IInternalRegulationObligationReviewStatus | null>(null);

  const { obligationsData, loading, pageInfo, handleFetchMore } = useObligations(statusFilter, onCompleted);

  useIntersectionObserver(sentinelRef, handleFetchMore);

  const handleStatusChange = (value: SelectOptionValue) => {
    if (value === 'ALL') {
      setStatusFilter(null);
      return;
    }
    const match = Object.values(IInternalRegulationObligationReviewStatus).find((v) => v === value);
    setStatusFilter(match ?? null);
  };

  const showNoObligationsAlert = !loading && (!obligationsData || obligationsData.length === 0);

  return (
    <SC.ObligationsList className={className}>
      <SC.FiltersHeader>
        <Select
          onChange={handleStatusChange}
          options={statusFilterOptions}
          value={statusFilter ?? 'ALL'}
          prefix="Status"
          data-testid="obligation-status-select"
        />
      </SC.FiltersHeader>
      {showNoObligationsAlert ? (
        <SC.AlertContainer>
          <Alert title="No obligations found." status="info" />
        </SC.AlertContainer>
      ) : (
        <SC.List>
          {obligationsData?.map(({ node: obligation }) => {
            const fragment = obligation.fragment?.edges[0]?.node;
            return (
              <SC.FragmentItem key={obligation.id}>
                <SC.FragmentTitle>
                  {fragment?.reference} | {fragment?.label}
                </SC.FragmentTitle>

                <CheckboxCard
                  id={obligation.id}
                  type={selectionMode}
                  isChecked={
                    selectionMode === 'radio'
                      ? activeObligation?.id === obligation.id
                      : selectedObligations?.some((o) => o.id === obligation.id)
                  }
                  isActive={activeObligation?.id === obligation.id}
                  onChange={
                    selectionMode === 'radio'
                      ? () => onUpdateActiveObligation?.(obligation)
                      : () => onToggleSelectedObligation?.(obligation)
                  }
                  onCardClick={selectionMode === 'checkbox' ? () => onUpdateActiveObligation?.(obligation) : undefined}
                >
                  <SC.ObligationContent>
                    <SC.ObligationTitle>{obligation.title}</SC.ObligationTitle>
                    <StatusBadge
                      status={
                        reviewedObligationIds?.has(obligation.id)
                          ? IInternalRegulationObligationReviewStatus.Reviewed
                          : activeRegulationTabId === REGULATION_TABS.REVIEW.id
                          ? obligation.reviewStatus
                          : obligation.mappingsReviewStatus
                      }
                    />
                  </SC.ObligationContent>
                </CheckboxCard>
              </SC.FragmentItem>
            );
          })}
          {loading && <SC.LoadingIndicator />}
          {pageInfo?.hasNextPage && <SC.Sentinel ref={sentinelRef} />}
        </SC.List>
      )}
    </SC.ObligationsList>
  );
};
